<script>

    export let sender = {}
    export let list = []
    export let baseUrl

    import { stepsStore } from '../store/stepsStore'
    import { fade } from 'svelte/transition'
    import { postHeaders } from '../store/antiforgery'

    const acceptTerms = async () => {

        // Record the signer's acceptance of the Electronic Record and Signature
        // Disclosure server-side so it lands in the audit trail / certificate.
        // Best-effort: never block the signer if the audit call fails.
        try {

            await fetch(`${baseUrl}/documents/consent`, {
                method: 'POST',
                headers: await postHeaders(baseUrl),
                body: JSON.stringify(list)
            })

        } catch (e) {

            console.error('Failed to record consent', e)

        }

        stepsStore.update( steps => {

            let copiedSteps = [...steps]

            copiedSteps[0].done = true

            return copiedSteps

        })

    }

</script>

<div in:fade class="terms-page" >

    <div class="terms-modal-wrap" >

        <div class="terms-modal" >

            <div class="terms-modal-head" >
                
                <h1 class="terms-modal-title" >Terms and Conditions</h1>
            
            </div>

            <div class="terms-modal-body" >

                <div>

                    <h2 class="terms-modal-heading" >ELECTRONIC RECORD AND SIGNATURE DISCLOSURE</h2>

                    <p class="terms-modal-paragraph" >Please read the following information carefully. By clicking the 'I agree' button, you agree that you have reviewed the following terms and conditions and consent to transact business electronically using Clinic HQ electronic signature system. If you do not agree to these terms, do not click the 'I agree' button.</p>

                </div>

                <div>

                    <h2 class="terms-modal-heading" >Legal validity of electronic signatures</h2>
                    <p class="terms-modal-paragraph" >Electronic signatures are legally recognized and enforceable in the United States under the Electronic Signatures in Global and National Commerce Act (ESIGN) and the Uniform Electronic Transactions Act (UETA). When you sign a document using the Clinic HQ electronic signature system, your electronic signature carries the same legal effect as a handwritten signature, provided you have consented to transact electronically as described in this disclosure.</p>

                </div>

                <div>

                    <h2 class="terms-modal-heading" >Electronic documents</h2>
                    <p class="terms-modal-paragraph" >Please note that { sender.name } (<a href="mailto:{ sender.email }">{ sender.email }</a>) ("we", "us" or "Company") will send all documents electronically to you to the email address that you have given us during the course of the business relationship unless you tell us otherwise in accordance with the procedure explained herein. Once you sign a document electronically, we will send a PDF version of the document to you.</p>

                </div>

                <div>

                    <h2 class="terms-modal-heading" >Request for paper copies</h2>
                    <p class="terms-modal-paragraph" >You have the right to request from us paper copies of the documents that are sent to you electronically. You have the ability to download and print documents that are sent to you electronically. If you wish to receive paper copies of the documents that we have sent to you electronically, you can send an email to <a href="mailto:{ sender.email }">{ sender.email }</a>.</p>

                </div>

                <div>

                    <h2 class="terms-modal-heading" >Withdrawing your consent</h2>
                    <p class="terms-modal-paragraph" >At any point in time during the course of our business relationship, you have the right to withdraw your consent to receive documents in electronic format. If you wish to withdraw your consent, you can decline to sign a document that we have sent to you and send an email to <a href="mailto:{ sender.email }">{ sender.email }</a> informing us that you wish to receive documents only in paper format. Upon request from you, we will stop sending documents using Clinic HQ electronic signature system.</p>

                </div>

                <div>

                    <h2 class="terms-modal-heading" >To advise { sender.name } of your new email address</h2>
                    <p class="terms-modal-paragraph" >If you need to change the email address that you use to receive notices and disclosures from us, write to us at <a href="mailto:{ sender.email }">{ sender.email }</a>.</p>

                </div>

                <div>

                    <h2 class="terms-modal-heading" > System requirements</h2>
                    <p class="terms-modal-paragraph" >To access, sign, and retain electronic documents sent through this system, you will need:</p>
                    <ul class="terms-modal-paragraph" style="list-style: disc; padding-left: 1.5rem;">
                        <li>A current version of a modern web browser (Chrome, Firefox, Safari, or Edge) with JavaScript and cookies enabled.</li>
                        <li>An active email account capable of receiving messages from { sender.email } and downloading PDF attachments.</li>
                        <li>Software capable of viewing and saving PDF documents (most modern browsers and operating systems include this by default).</li>
                        <li>A reliable internet connection.</li>
                        <li>A device with a screen large enough to read the document and an input method (mouse, touchscreen, or stylus) to draw a signature.</li>
                        <li>Sufficient local storage or printing capability to retain a copy of the signed document and certificate of completion for your records.</li>
                    </ul>
                    <p class="terms-modal-paragraph" >If the hardware or software requirements change in a way that creates a material risk that you will not be able to access or retain documents, we will notify you at the email address on file and give you the opportunity to withdraw your consent without penalty.</p>

                </div>

                <div>

                    <h2 class="terms-modal-heading" >How we identify you</h2>
                    <p class="terms-modal-paragraph" >You access each document through a unique link that we send to the email address on file for you. That link contains a unique reference number that identifies your document. We use the email address to which the document was sent as the method of identifying you, and this identity method is recorded with your signed document.</p>

                </div>

                <div>

                    <h2 class="terms-modal-heading" >Your signature and certificate of completion</h2>
                    <p class="terms-modal-paragraph" >When you sign, your signature is affixed to the document and Clinic HQ generates a Certificate of Completion. The certificate is identified by a unique reference number (a globally unique identifier, or GUID) that can be used to locate the record of your transaction, including who signed the document and when it was signed. A copy of the certificate is provided to you together with your signed document.</p>

                </div>

                <div>

                    <h2 class="terms-modal-heading" >Document authenticity</h2>
                    <p class="terms-modal-paragraph" >To protect the integrity of your documents, Clinic HQ records a cryptographic checksum (a digital fingerprint) of the document both before and after it is signed, and includes both values on the Certificate of Completion. If a question ever arises about whether a document was altered, these records can be compared to confirm that the signed document has not been changed. Each completed document is also sealed with a digital signature backed by a private cryptographic certificate, and, where available, a trusted timestamp.</p>

                </div>

                <div>

                    <h2 class="terms-modal-heading" >Audit trail</h2>
                    <p class="terms-modal-paragraph" >Clinic HQ maintains an audit trail for every document. From the moment a document is sent until it is completed, we record key events — including when the document was sent, opened, and signed — along with the date and time, the IP address, and the web browser and operating system used. This audit trail is included on the Certificate of Completion.</p>

                </div>

                <div>

                    <h2 class="terms-modal-heading" >Secure records</h2>
                    <p class="terms-modal-paragraph" >Your documents are transmitted over an encrypted (HTTPS/TLS) connection and stored in Microsoft Azure cloud storage with encryption at rest. Access to download your documents is protected by time-limited, signed links.</p>

                </div>

            </div>

        </div>

    </div>

    <div class="terms-page-foot" >
        <button class="b-main space-2" on:click={acceptTerms} >I Agree</button>
    </div>

</div>

<style>
    .terms-page {
        @apply h-auto bg-gray-500;
    }

    .terms-modal-wrap {
        @apply h-auto;
    }

    .terms-modal {
        @apply h-auto max-w-screen-lg relative bg-white mx-auto tracking-widest;
    }

    .terms-modal-head {
        @apply p-6 px-10 bg-gray-200 sticky top-30;
    }

    .terms-modal-title {
        @apply text-lg;
    }

    .terms-modal-body {
        @apply pt-6 px-10 bg-white;
    }

    .terms-modal-heding {
        @apply text-lg font-medium;
    }

    .terms-modal-paragraph {
        @apply text-sm text-gray-700 py-4;
    }

    .terms-page-foot {
        @apply flex-none w-full h-20 sticky bottom-0 bg-white max-w-screen-lg mx-auto flex items-center justify-end px-6 border-t border-solid border-gray-300;
    }

    .space-2 {
        @apply ml-4;
    }

@media (min-width: 786px) {

    .terms-page {
        @apply h-full flex flex-col;
    }

    .terms-modal-wrap {
        @apply p-10 overflow-hidden;
    }

    .terms-modal {
        @apply h-full mx-auto rounded-lg overflow-auto;
    }

    .terms-modal-head {
        @apply sticky top-0;
    }

    .terms-page-foot {
        @apply px-0 bg-transparent border-t-0;
    }
}

</style>